import React, { useState } from 'react';
import { isFormIncomplete } from '../../../helpers/formState';
import { Form, Input, Select, Row, Col, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import {
  getAggregatedAddress,
  getBaseUrl,
  handleNumericKeyPress,
  isUndefinedNullOrEmpty,
} from '../../../common/common';
import { OrderStatus } from '../../../util/util';
import {
  validateBankDraft,
  updateOrder,
  updateErrorMsg,
  savePaymentInfo,
} from '../../../store/action/OrderAction';
import { getBankDraftPaymentInfoModel } from '../../../helpers/OrderHelperFunctions';
import { useCartData, useLoading, useServiceablitityData, useSessionData } from '../../../hooks/order';
import useLeadsSDK from '../../../hooks/useLeadsSDK';
import useRevalidateOnLanguageChange from '../../../hooks/useRevalidateOnLanguageChange';
import { TermsContent } from '../../common/TermsInfo';
import useWindowDimensions from '../../common/useWindowDimensions';
import MobileTermsCheckbox from './MobileTermsCheckbox';
import TermsAgreementText from './TermsAgreementText';
import OrderErrorMessage from './OrderErrorMessage';

const { Option } = Select;

const BankDraftForm = ({ submitOrderRef }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  useRevalidateOnLanguageChange(form);
  const { isMobile } = useWindowDimensions();
  // Consent is an explicit opt-in - both boxes start unchecked and Place Order stays
  // disabled until the customer ticks them.
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isApaChecked, setIsApaChecked] = useState(false);

  const dispatch = useDispatch();
  const leads = useLeadsSDK();
  const { loading } = useLoading();
  const { servRef } = useCartData();
  const { orderInfo } = useSessionData();
  const { normalizedAddress } = useServiceablitityData();
  const {
    orderAccessToken,
    billingAddress,
    serviceAddress,
    billingAdd1,
    billingAdd2,
    billingCity,
    billingState,
    billingZip,
  } = orderInfo;
  // Bank draft has no billing-address fields of its own, so it bills to whatever the order
  // already carries - the service address unless a different billing address was saved.
  const isAddressSame = isUndefinedNullOrEmpty(billingAddress) || billingAddress === serviceAddress;

  // Same as the e-commerce SPC: once the bank account validates and the order is updated,
  // Place Order submits the order directly - the terms/APA boxes on this form are the consent.
  // Deferred a tick so CartInformation has re-rendered with the saved bank payment info and
  // the updated billing address before its handler (published to submitOrderRef) reads them.
  const submitOrder = () => {
    setTimeout(() => {
      submitOrderRef?.current?.(true);
    }, 0);
  };

  const setAccountNumberError = (message) => {
    form.setFields([{ name: 'accountNumber', errors: [message] }]);
  };

  const handleContinue = (values) => {
    if (isUndefinedNullOrEmpty(orderAccessToken)) {
      return;
    }
    const { routingNumber, accountNumber, accountType } = values;
    const customerAgreementTimestamp = moment().format('lll');
    const billAddr1 = isAddressSame ? normalizedAddress.address1 : billingAdd1;
    const billAddr2 = isAddressSame ? normalizedAddress.address2 : billingAdd2;
    const billCity = isAddressSame ? normalizedAddress.city : billingCity;
    const billState = isAddressSame ? normalizedAddress.state : billingState;
    const billZip = isAddressSame ? normalizedAddress.zipCode : billingZip;

    const bankBody = {
      applClient: 'ELNKWWW',
      bankAccountNum: btoa(accountNumber),
      bankRoutingNum: btoa(routingNumber),
      bankAccountType: accountType,
      address1: billAddr1?.trim() || '',
      address2: billAddr2?.trim() || '',
      city: billCity?.trim() || '',
      state: billState?.trim() || '',
      zip: billZip?.trim() || '',
    };

    // Clear a banner left by an earlier failed attempt before validating again.
    dispatch(updateErrorMsg(''));
    dispatch(validateBankDraft(bankBody))
      .then((response) => {
        const validationError = response?.value?.data?.validationError;
        const isValid = !validationError || Object.keys(validationError).length === 0;

        if (!isValid) {
          dispatch(updateOrder({ serviceError: t('singlePageCheckout.invalidAccountNumber') }, orderAccessToken));
          setAccountNumberError(t('singlePageCheckout.invalidAccountNumber'));
          return;
        }

        const maskedBankAccountNum = accountNumber.slice(-4).padStart(accountNumber.length, '*');
        dispatch(savePaymentInfo(getBankDraftPaymentInfoModel({
          agentReadTC: true,
          customerAcceptedTC: false,
          bankAccountType: accountType,
          maskedBankAccountNum,
          abaNum: bankBody.bankRoutingNum,
          bankAccountNum: bankBody.bankAccountNum,
          paymentType: 'bankDraft',
        })));

        dispatch(
          updateOrder(
            {
              orderStatus: OrderStatus.ccInfo,
              disclaimerRead: customerAgreementTimestamp,
              autoPaymentAgreed: customerAgreementTimestamp,
              serviceError: null,
              billingAdd1: billAddr1,
              billingAdd2: billAddr2,
              billingAddress: getAggregatedAddress({
                address1: billAddr1,
                address2: billAddr2,
                city: billCity,
                state: billState,
                zipCode: billZip,
              }),
              billingCity: billCity,
              billingState: billState,
              billingZip: billZip,
              serviceReference: servRef,
            },
            orderAccessToken
          )
        )
          .then(() => {
            dispatch(updateErrorMsg(''));
            leads?.updateOrder?.('Update Order: Set Billing Address', {
              payment: {
                billingAddress: {
                  address1: billAddr1,
                  address2: billAddr2,
                  city: billCity,
                  state: billState,
                  zip: billZip,
                },
              },
            });
            submitOrder();
          })
          .catch((err) => {
            dispatch(updateErrorMsg(err));
          });
      })
      .catch(() => {
        // The request itself failed (e.g. the payment service timed out), so the account
        // number wasn't judged invalid. Show the generic order-processing error
        // (checkout.tcError, rendered by ReviewPaySection) instead of a field error.
        dispatch(updateOrder({ serviceError: 'bank-draft-validate failed' }, orderAccessToken));
        dispatch(updateErrorMsg('bank-draft-validate failed'));
      });
  };

  return (
    <div className="your-info-tab payment-info same-common">
      <Form
        name="bankDraft"
        onFinish={handleContinue}
        autoComplete="off"
        form={form}
        validateTrigger={'onBlur'}
        className="form-info-tab"
      >
        <Row className="yor-info-row j-between">
          <Col span={12} className="your-info-input responsive-width-set">
            <Form.Item
              label={t('singlePageCheckout.bankRoutingNumber')}
              name="routingNumber"
              rules={[
                { required: true, message: t('singlePageCheckout.enterRoutingNumber') },
                { min: 8, max: 9, message: t('singlePageCheckout.invalidRoutingNumber') },
              ]}
            >
              <Input
                maxLength={9}
                className="input-box"
                onKeyPress={handleNumericKeyPress}
                placeholder={t('singlePageCheckout.routingNumberPlaceholder')}
              />
            </Form.Item>
          </Col>
          <Col span={12} className="your-info-input responsive-width-set">
            <Form.Item
              label={t('singlePageCheckout.bankAccountNumber')}
              name="accountNumber"
              rules={[
                { required: true, message: t('singlePageCheckout.enterAccountNumber') },
                { min: 5, message: t('singlePageCheckout.accountNumberMinLength') },
              ]}
            >
              <Input
                maxLength={17}
                className="input-box"
                onKeyPress={handleNumericKeyPress}
                placeholder={t('singlePageCheckout.accountNumberPlaceholder')}
              />
            </Form.Item>
          </Col>
          <Col span={12} className="your-info-input responsive-width-set">
            <Form.Item
              label={t('singlePageCheckout.reEnterBankAccountNumber')}
              name="confirmAccountNumber"
              dependencies={['accountNumber']}
              rules={[
                { required: true, message: t('singlePageCheckout.reEnterAccountNumber') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('accountNumber') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('singlePageCheckout.accountNumbersNoMatch')));
                  },
                }),
              ]}
            >
              <Input
                maxLength={17}
                className="input-box"
                onKeyPress={handleNumericKeyPress}
                placeholder={t('singlePageCheckout.accountNumberPlaceholder')}
              />
            </Form.Item>
          </Col>
          <Col span={12} className="your-info-input responsive-width-set">
            <Form.Item
              label={t('singlePageCheckout.selectAccountType')}
              name="accountType"
              initialValue="checking"
            >
              <Select>
                <Option value="checking">{t('singlePageCheckout.checkingAccount')}</Option>
                <Option value="savings">{t('singlePageCheckout.savingsAccount')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {isMobile ? (
          <MobileTermsCheckbox checked={isTermsChecked} onChange={setIsTermsChecked} />
        ) : (
          <>
            <div className="spc-plan-commitment-info">
              <TermsContent />
            </div>

            <div style={{ marginTop: '16px' }}>
              <Checkbox
                className="spc-terms-checkbox"
                checked={isTermsChecked}
                onChange={(e) => setIsTermsChecked(e.target.checked)}
              >
                <span>
                  <TermsAgreementText />
                </span>
              </Checkbox>
            </div>
          </>
        )}

        <div style={{ marginTop: '12px' }}>
          <Checkbox
            className="apa-agreement-checkbox"
            checked={isApaChecked}
            onChange={(e) => setIsApaChecked(e.target.checked)}
          >
            <span>
              {t('payment.apaAgreementPre')}{' '}
              <a
                href={getBaseUrl() + 'tcs/?apa=1/'}
                target="_blank"
                rel="noreferrer"
                className="payment-authorization-url"
                onClick={(e) => e.stopPropagation()}
              >
                {t('payment.automaticPayments')}
              </a>{' '}
              {t('payment.apaAgreementPost')}
            </span>
          </Checkbox>
        </div>

        <OrderErrorMessage />

        <div className="enter-your-info payment-submit spc-payment-submit-left" style={{ marginTop: '16px' }}>
          <Form.Item shouldUpdate noStyle>
            {() => (
              <button
                className="blue-button"
                htmlType="submit"
                disabled={
                  loading || !isTermsChecked || !isApaChecked
                  || isFormIncomplete(form, ['routingNumber', 'accountNumber', 'confirmAccountNumber'])
                }
              >
                {loading ? t('common.processing') : t('singlePageCheckout.placeOrder')}
              </button>
            )}
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default BankDraftForm;
